"use client";

import { useRouter } from "@/i18n/navigation";
import { useFlightStore } from "@/store/flight-store";
import { countryCodes } from "@/utils/countryCodes";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import React, { useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { toast } from "sonner";

export const QuotationConfirmationModal = ({ flight, price, query }) => {
  const { locale } = useParams();
  const t = useTranslations("form");
  const { quoteData } = useFlightStore();
  const router = useRouter();
  const [userData, setUserData] = useState({
    fName: "",
    lName: "",
    email: "",
    mobile: "",
    code: "+966",
  });
  const [error, setError] = useState({
    status: false,
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  function hideError() {
    setTimeout(() => {
      setError({
        status: false,
        message: "",
      });
    }, 3000);
  }

  const submitHandler = async (e: any) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (
        !userData.fName ||
        !userData.lName ||
        !userData.email ||
        !userData.mobile
      ) {
        setError({ status: true, message: "*All fields are mandatory!" });
        hideError();
        setIsLoading(false);
      } else {
        const prefixedNumber = `PFS-${randomNumber}`;
        userData.code = userData.code ? userData.code : "+966";

        const data = await fetch("/api/v1/request", {
          method: "POST",
          body: JSON.stringify({
            query,
            prefixedNumber,
            userData,
            flight,
            price,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const response = await data.json();
        if (response) {
          setIsLoading(false);
          toast.success(
            "Your Quotation has been submitted! Redirecting to home page..."
          );
          router.replace("/");
          buildWhatsAppMessageLink(userData, flight, prefixedNumber);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Construct the WhatsApp link
  function buildWhatsAppMessageLink(
    userData: { fName: any; lName: any; email: any; mobile?: string },
    flightData: { categoryName: any; flights: any },
    quoteNumber: string
  ) {
    const number = "+966920003455";
    const message = `Hi! My name is ${userData.fName} ${userData.lName}. \n My email is ${userData.email}. I have already sent you a mail for quotation of ${flightData.categoryName} with reference No. ${quoteNumber}. Awaiting for your response. Thanks`;
    const whatsappLink = `https://api.whatsapp.com/send?phone=${encodeURIComponent(
      number
    )}&text=${encodeURIComponent(message)}`;

    // Open the WhatsApp link in a new tab/window
    window.open(whatsappLink, "_blank");
  }

  // Event handler to update the userData state on input changes
  const handleChange = (e: { target: { id: any; value: any } }) => {
    const { id, value } = e.target;
    setUserData((prevUserData) => ({
      ...prevUserData,
      [id]: value,
    }));
  };

  // Function to generate a random 6-digit number
  function generateRandomNumber() {
    return Math.floor(Math.random() * 900000) + 100000; // Generates a number between 100000 and 999999
  }

  // Generate the random number
  const randomNumber = generateRandomNumber();
  return (
    <>
      <div className="flex flex-col items-start gap-4 ">
        <div className="w-12 h-12 shrink-0 rounded-lg bg-[#efefef] flex items-center justify-center">
          <FaFileInvoiceDollar className=" text-[#5a558e] text-xl" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-800">{t("title")}</h3>
          <p className="mb-6 text-sm text-gray-600 max-w-[90%]">{t("desc")}</p>
        </div>
      </div>
      <form
        onSubmit={submitHandler}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div className="flex flex-col justify-start items-start gap-2 col-span-2 md:col-span-1">
          <label htmlFor="fName" className="text-gray-800 text-sm font-medium">
            {t("fName")}
          </label>
          <div className="w-full rounded border border-black/20 bg-white focus-within:border-[#5a558e] focus-within:ring-2 focus-within:ring-[#5a558e]/20">
            <input
              type="text"
              id="fName"
              value={userData.fName}
              onChange={(e) =>
                setUserData({ ...userData, fName: e.target.value })
              }
              className="px-4 py-2 w-full rounded bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col justify-start items-start gap-2 col-span-2 md:col-span-1">
          <label htmlFor="lName" className="text-gray-800 text-sm font-medium">
            {t("lName")}
          </label>
          <div className="w-full rounded border border-black/20 bg-white focus-within:border-[#5a558e] focus-within:ring-2 focus-within:ring-[#5a558e]/20">
            <input
              type="text"
              id="lName"
              value={userData.lName}
              onChange={(e) =>
                setUserData({ ...userData, lName: e.target.value })
              }
              className="px-4 py-2 w-full rounded bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col justify-start items-start gap-2 col-span-2">
          <label htmlFor="email" className="text-gray-800 text-sm font-medium">
            {t("email")}
          </label>
          <div className="w-full rounded border border-black/20 bg-white focus-within:border-[#5a558e] focus-within:ring-2 focus-within:ring-[#5a558e]/20">
            <input
              type="email"
              id="email"
              value={userData.email}
              onChange={(e) =>
                setUserData({ ...userData, email: e.target.value })
              }
              className="px-4 py-2 w-full rounded bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-col justify-start items-start gap-2 col-span-2">
          <label htmlFor="mobile" className="text-gray-800 text-sm font-medium">
            {t("mobile")}
          </label>
          <div className="flex items-center gap-2 w-full rounded border border-black/20 bg-white focus-within:border-[#5a558e] focus-within:ring-2 focus-within:ring-[#5a558e]/20">
            <select
              id="code"
              value={userData.code}
              onChange={(e) =>
                setUserData({ ...userData, code: e.target.value })
              }
              className="px-4 py-2 rounded text-gray-800 text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
            >
              {countryCodes.map(({ dial_code }, index) => {
                return (
                  <option
                    key={index}
                    value={dial_code}
                    className="text-gray-800 text-sm"
                  >
                    {dial_code}
                  </option>
                );
              })}
            </select>
            <input
              type="tel"
              id="mobile"
              value={userData.mobile}
              onChange={(e) =>
                setUserData({ ...userData, mobile: e.target.value })
              }
              className="px-4 py-2 w-full bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 text-sm"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="bg-[#5a558e] text-white py-2 px-4 text-sm font-medium mt-4 rounded shadow-sm flex items-center gap-2 w-full md:w-auto justify-center text-center col-span-2"
        >
          {isLoading && (
            <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
          )}
          {isLoading
            ? locale === "en"
              ? "Please wait..."
              : "يرجى الإنتظار..."
            : t("submit")}
        </button>
      </form>
    </>
  );
};
